import { pathOr } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useInterval } from 'react-use';

import { PART_STATUSES, PROCESSING_CATEGORIES } from '@frontend/api';

import { assemblyTree } from 'modules/quotes/quoteItemComponents/assemblyTree/data/actions';
import * as quoteItemComponentsActions from 'modules/quotes/quoteItemComponents/data/actions';
import { getExpandedComponentId } from 'modules/quotes/quoteItemComponents/data/reducer';

import partService from 'services/partService';
import quoteService from 'services/quoteService';

import { GlobalStore } from 'store/configureStore';
import { receiveSupplierMaterialFromGetOrCreate } from 'store/materials/actions';

import { queryClient } from 'utils/QueryClientProvider';
import { isStatusInProgress } from 'utils/partUtils';

import * as actions from './actions';
import { initialBriefComponentFetch } from './actions';
import * as selectors from './selectors';
import quoteItemService from './service';

export const COMPONENT_QUERY_KEYS = {
  SIMILAR_PARTS: 'similarParts',
  JOB_HISTORY: 'jobHistory',
  QUOTE_HISTORY: 'quoteHistory',
};

export const getSimilarPartsKey = (componentId) => [
  COMPONENT_QUERY_KEYS.SIMILAR_PARTS,
  componentId,
];
export const getJobHistoryKey = (componentId) => [
  COMPONENT_QUERY_KEYS.JOB_HISTORY,
  componentId,
];
export const getQuoteHistoryKey = (componentId) => [
  COMPONENT_QUERY_KEYS.QUOTE_HISTORY,
  componentId,
];

/**
 * @deprecated Use useGetSimilaritySearch from @frontend/api instead
 */
export const useSimilarPartsQuery = (
  componentId,
  partUuid,
  partNumber,
  options,
) => {
  const getSimilarPartsCallback = useCallback(
    () => partService.similaritySearch(partUuid, { partNumber }),
    [partNumber, partUuid],
  );
  return useQuery(getSimilarPartsKey(componentId), getSimilarPartsCallback, {
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useJobHistoryQuery = (componentId, partUuid, options) => {
  const getJobHistoryCallback = useCallback(
    () => partService.getJobs(partUuid),
    [partUuid],
  );
  return useQuery(getJobHistoryKey(componentId), getJobHistoryCallback, {
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQuoteHistoryQuery = (componentId, partUuid, options) => {
  const getQuoteHistoryCallback = useCallback(
    () => partService.getQuotes(partUuid),
    [partUuid],
  );
  return useQuery(getQuoteHistoryKey(componentId), getQuoteHistoryCallback, {
    refetchOnWindowFocus: false,
    ...options,
  });
};

const refreshComponent = async (componentId, params) => {
  const component = await quoteService.components.get(componentId, params);
  GlobalStore.dispatch(actions.receiveComponent(component));
  return component;
};

const refreshBriefComponent = async (componentId) => {
  const newBriefComponent = await quoteService.components.getBrief(componentId);
  const serializedBriefComponent = { ...newBriefComponent };
  // NOTE: the getBrief endpoint returns additional information on the brief component
  // about its descendant child component ids. We use this information to fetch its child
  // brief components to update the store.
  // we are deleting it here on the brief component we are putting in the store to minimize
  // confusion for future developers. This will prevent the briefcomponent schema from being
  // polymorphic
  delete serializedBriefComponent.childComponentIds;
  GlobalStore.dispatch(
    actions.briefComponent.receive({
      briefComponent: serializedBriefComponent,
    }),
  );
  return newBriefComponent;
};

const bulkRefreshBriefComponents = async (quoteItemId, componentIds) => {
  const newBriefComponents = await quoteItemService.briefComponentsList(
    quoteItemId,
    componentIds,
  );
  GlobalStore.dispatch(
    actions.briefComponent.receiveMultiple({
      briefComponents: newBriefComponents,
    }),
  );
};

const refreshExpandedComponent = async () => {
  const expandedComponentId = getExpandedComponentId(GlobalStore.getState());
  if (expandedComponentId) {
    const response = await quoteService.components.get(expandedComponentId);
    GlobalStore.dispatch(
      quoteItemComponentsActions.expandComponent.success({
        component: response,
      }),
    );
  }
};

const refreshBomRollupForComponentParentsById = ({
  componentId,
  delayTime = 500,
}) => {
  const briefComponent = selectors.getBriefComponentById(
    GlobalStore.getState(),
    componentId,
  );
  if (briefComponent) {
    const { parentComponentIds } = briefComponent;
    if (parentComponentIds.length > 0) {
      GlobalStore.dispatch(
        actions.refreshBomRollup.request({
          parentComponentIds,
          delayTime,
        }),
      );
    }
  }
};

const handleComponentInterrogationStatusChange = async ({
  oldStatusInfo,
  newStatusInfo,
  componentId,
}) => {
  const significantChange =
    isStatusInProgress(oldStatusInfo.status) &&
    !isStatusInProgress(newStatusInfo);

  if (significantChange) {
    const isRootComponent = selectors.getIsRootComponentFromComponentId(
      GlobalStore.getState(),
      componentId,
    );
    if (isRootComponent) {
      refreshComponent(componentId);
    } else {
      const quoteItemId = selectors.getQuoteItemIdFromComponentId(
        GlobalStore.getState(),
        componentId,
      );
      const expandedQuoteItemId = selectors.getExpandedQuoteItemId(
        GlobalStore.getState(),
      );
      const expandedComponentId = getExpandedComponentId(
        GlobalStore.getState(),
      );

      const isQuoteItemExpanded =
        expandedQuoteItemId && quoteItemId == expandedQuoteItemId;
      const isComponentExpanded =
        (isRootComponent && isQuoteItemExpanded) ||
        expandedComponentId == componentId;
      if (isQuoteItemExpanded) {
        if (isComponentExpanded) {
          await refreshExpandedComponent();
        }
      }
      refreshBomRollupForComponentParentsById({
        componentId,
        delayTime: 1000,
      });
    }
  }
};

const handleComponentProcessingStatusChange = async ({
  oldStatusInfo,
  newStatusInfo,
  componentId,
}) => {
  const finishedProcessing =
    oldStatusInfo[PROCESSING_CATEGORIES.PROCESSING].status !==
      PART_STATUSES.FINISHED &&
    newStatusInfo[PROCESSING_CATEGORIES.PROCESSING].status ===
      PART_STATUSES.FINISHED;
  const finishedIndexing =
    oldStatusInfo[PROCESSING_CATEGORIES.GEOMETRIC_INDEXING].status !==
      PART_STATUSES.FINISHED &&
    newStatusInfo[PROCESSING_CATEGORIES.GEOMETRIC_INDEXING].status ===
      PART_STATUSES.FINISHED;

  if (finishedProcessing || finishedIndexing) {
    const quoteItemId = selectors.getQuoteItemIdFromComponentId(
      GlobalStore.getState(),
      componentId,
    );
    const expandedQuoteItemId = selectors.getExpandedQuoteItemId(
      GlobalStore.getState(),
    );
    const expandedComponentId = getExpandedComponentId(GlobalStore.getState());
    const isRootComponent = selectors.getIsRootComponentFromComponentId(
      GlobalStore.getState(),
      componentId,
    );
    const isQuoteItemExpanded =
      expandedQuoteItemId && quoteItemId == expandedQuoteItemId;
    const isComponentExpanded =
      (isRootComponent && isQuoteItemExpanded) ||
      expandedComponentId == componentId;

    if (finishedProcessing) {
      // need to make sure we fetch any new child components that may have appeared as a
      // result of processing completion, and also ensure that we refresh rollup costs
      GlobalStore.dispatch(assemblyTree.markNeedsUpdate({}));

      if (isRootComponent) {
        const refreshedQuoteItem = await quoteService.quoteItems.get(
          quoteItemId,
        );
        GlobalStore.dispatch(
          initialBriefComponentFetch.request({ quoteItemId }),
        );
        GlobalStore.dispatch(actions.receiveQuoteItem(refreshedQuoteItem));

        // also need to serialize the material details back for the materials store
        // only on the first update
        const { rootComponent } = refreshedQuoteItem;
        const { material } = rootComponent;
        if (material) {
          const newSupplierMaterial = {
            ...material,
            materialId: material.material.id,
          };
          GlobalStore.dispatch(
            receiveSupplierMaterialFromGetOrCreate({
              supplierMaterial: newSupplierMaterial,
            }),
          );
        }
      } else if (isQuoteItemExpanded) {
        const briefComponent = await refreshBriefComponent(componentId);
        // if the component has children, we need to fetch the information for the child
        // components and serialize them into the store
        const { childComponentIds } = briefComponent;
        if (!!childComponentIds && childComponentIds.length > 0) {
          await bulkRefreshBriefComponents(quoteItemId, childComponentIds);
        }

        if (isComponentExpanded) {
          await refreshExpandedComponent();
        }

        GlobalStore.dispatch(
          actions.refreshBomRollup.request({
            parentComponentIds: briefComponent.parentComponentIds,
            delayTime: 1000,
          }),
        );
      }
    }

    if (finishedIndexing && (isRootComponent || isComponentExpanded)) {
      queryClient.invalidateQueries(getSimilarPartsKey(componentId));
    }
  }
};

const parseNewProcessingStatuses = ({
  oldProcessingStatuses,
  nextProcessingStatuses,
}) => {
  let areAnyDifferent = false;
  for (const [componentId, newStatusInfo] of Object.entries(
    nextProcessingStatuses,
  )) {
    const oldStatusInfo = oldProcessingStatuses[componentId];
    if (!oldStatusInfo) {
      areAnyDifferent = true;
    } else {
      let anyDifferentLocal = false;
      for (const category of Object.values(PROCESSING_CATEGORIES)) {
        // TODO: add timing considerations
        if (
          pathOr(null, [category, 'status'], newStatusInfo) !==
          pathOr(null, [category, 'status'], oldStatusInfo)
        ) {
          anyDifferentLocal = true;
        }
      }
      if (anyDifferentLocal) {
        areAnyDifferent = true;
        handleComponentProcessingStatusChange({
          oldStatusInfo,
          newStatusInfo,
          componentId,
        });
      }
    }
  }
  if (areAnyDifferent) {
    GlobalStore.dispatch(
      actions.componentStatuses.receiveProcessingStatuses({
        processingStatuses: nextProcessingStatuses,
      }),
    );
  }
};

const parseNewInterrogationStatuses = async ({
  nextInterrogationStatuses,
  oldInterrogationStatuses,
}) => {
  let areAnyDifferent = false;
  for (const [componentId, newStatusInfo] of Object.entries(
    nextInterrogationStatuses,
  )) {
    const oldStatusInfo = oldInterrogationStatuses[componentId];
    // TODO: timing considerations
    if (!oldStatusInfo) {
      areAnyDifferent = true;
    } else if (oldStatusInfo.status !== newStatusInfo.status) {
      areAnyDifferent = true;
      handleComponentInterrogationStatusChange({
        oldStatusInfo,
        newStatusInfo,
        componentId,
      });
    }
  }
  if (areAnyDifferent) {
    GlobalStore.dispatch(
      actions.componentStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextInterrogationStatuses,
      }),
    );
  }
};

const checkPendingsStatuses = async ({
  pendingProcessingEntries,
  pendingInterrogationEntries,
}) => {
  const oldProcessingStatuses = selectors.getAllComponentProcessingStatuses(
    GlobalStore.getState(),
  );
  const oldInterrogationStatuses =
    selectors.getAllComponentInterrogationStatuses(GlobalStore.getState());
  // establish mapping
  const uuidToComponentId = {};
  Object.entries(oldProcessingStatuses).forEach(([componentId, entry]) => {
    uuidToComponentId[entry.uuid] = componentId;
  });
  Object.entries(oldInterrogationStatuses).forEach(([componentId, entry]) => {
    uuidToComponentId[entry.uuid] = componentId;
  });
  const primaryFileUuids = [
    ...new Set(
      pendingProcessingEntries
        .concat(pendingInterrogationEntries)
        .map((entry) => entry.uuid),
    ),
  ];
  const response = await partService.checkStatuses(primaryFileUuids);
  const { processing, interrogation } = response.statuses;
  const nextProcessingStatuses = {};
  const nextInterrogationStatuses = {};
  for (const i in processing) {
    const componentId = uuidToComponentId[processing[i].uuid];
    nextProcessingStatuses[componentId] = {
      ...processing[i],
    };
    nextInterrogationStatuses[componentId] = {
      ...interrogation[i],
    };
  }

  parseNewProcessingStatuses({ oldProcessingStatuses, nextProcessingStatuses });
  parseNewInterrogationStatuses({
    oldInterrogationStatuses,
    nextInterrogationStatuses,
  });
};

export const usePeriodicallyCheckBAQStatuses = () => {
  const oldProcessingStatuses = useSelector(
    selectors.getAllComponentProcessingStatuses,
  );
  const oldInterrogationStatuses = useSelector(
    selectors.getAllComponentInterrogationStatuses,
  );

  const pendingProcessingEntries = useMemo(
    () =>
      Object.values(oldProcessingStatuses).filter((entry) =>
        Object.values(PROCESSING_CATEGORIES).some((category) =>
          isStatusInProgress(entry[category]?.status),
        ),
      ),
    [oldProcessingStatuses],
  );
  const pendingInterrogationEntries = useMemo(
    () =>
      Object.values(oldInterrogationStatuses).filter((entry) =>
        isStatusInProgress(entry.status),
      ),
    [oldInterrogationStatuses],
  );

  const shouldCheck =
    pendingProcessingEntries.length > 0 ||
    pendingInterrogationEntries.length > 0;

  useInterval(
    () =>
      checkPendingsStatuses({
        pendingProcessingEntries,
        pendingInterrogationEntries,
      }),
    shouldCheck ? 2000 : null,
  );
};
